import { Views } from '@singularsystems/neo-react';
import { List, NotifyUtils } from '@singularsystems/neo-core';
import Beneficiary from 'st-common/Participants/Models/Beneficiary';
import { AppService, Types } from '../ParticipantsTypes';
import { BeneficiaryModalVM } from 'st-common/Participants/Components/BeneficiaryModal';
import { NotificationDuration } from 'st-app/Models/Enums/NotificationDuration';

export default class BeneficiariesVM extends Views.ViewModelBase {
    public beneficiaryModalVM = this.registerViewModel(BeneficiaryModalVM);

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public authService = AppService.get(Types.Shared.Services.STAuthenticationService),
        public apiClient = AppService.get(Types.Participants.ApiClients.BeneficiariesApiClient),
        public configService = AppService.get(Types.Participants.Services.ParticipantConfigService),
        public participantsDataCache = AppService.get(Types.Participants.Services.DataCache)) {

        super(taskRunner);

        this.beneficiaryModalVM.saveCallback = this.saveBeneficiaries.bind(this);
        this.makeObservable();
    }

    public beneficiaries = new List(Beneficiary);

    public get isBeneficiariesEnabled(): boolean {
        return this.configService.isLoaded && this.configService.enableBeneficiaries;
    }

    public async initialise() {
        const result = await this.taskRunner.waitFor(this.apiClient.getMyBeneficiaries());
        if (result?.data) {
            this.beneficiaries.update(result.data);
        }
    }

    public addBeneficiary() {
        this.beneficiaryModalVM.showAdd(this.beneficiaries, null, this.defaultPercentForNewBeneficiary());
    }

    public editGuardian(beneficiary: Beneficiary) {
        this.beneficiaryModalVM.editGuardian(beneficiary);
    }

    public async saveBeneficiaries() {
        if (!this.isTotalPercentValid()) {
            return;
        }

        this.taskRunner.run(async () => {
            const result = await this.apiClient.saveMyBeneficiaries(this.beneficiaries.toJSArray());
            if (result?.data) {
                this.beneficiaries.update(result.data);
                NotifyUtils.addSuccess("Beneficiary Saved", "Beneficiary has been saved successfully.", NotificationDuration.Standard);
            }
        });
    }

    public async deleteBeneficiary(beneficiary: Beneficiary) {
        this.beneficiaries.remove(beneficiary);

        this.taskRunner.run(async () => {
            const result = await this.apiClient.saveMyBeneficiaries(this.beneficiaries.toJSArray());
            if (result?.data) {
                this.beneficiaries.update(result.data);
                NotifyUtils.addSuccess("Beneficiary Deleted", "Beneficiary has been deleted successfully.", NotificationDuration.Standard);
            }
        });
    }

    public editBeneficiary(beneficiary: Beneficiary) {
        this.beneficiaryModalVM.showEdit(beneficiary, this.beneficiaries);
    }

    private defaultPercentForNewBeneficiary() {
        const remainingPercent = 100 - this.totalSharePercentage;
        const defaultPercent = remainingPercent > 0 ? remainingPercent : 0;
        return defaultPercent;
    }

    private isTotalPercentValid() {
        if (this.totalSharePercentage !== 100) {
            NotifyUtils.addDanger("Share Percentage Error", "Total share percentage must equal 100% before saving.", NotificationDuration.Standard);

            return;
        }

        return true;
    }

    public get totalSharePercentage(): number {
        let total = 0;
        this.beneficiaries.forEach(b => {
            total += b.sharePercentage || 0;
        });
        return total;
    }
}